import Vue from 'vue';

const state = Vue.observable({
  rates: null,
  loading: false,
  lastAttempt: 0
});

const messages = {
  'zh-CN': {
    loading: '正在获取参考汇率；暂显示人民币',
    unavailable: '汇率暂不可用；显示人民币',
    reference: '参考汇率日期：'
  },
  'ja-JP': {
    loading: '参考レートを取得中。人民元で表示しています',
    unavailable: 'レートを取得できません。人民元で表示しています',
    reference: '参考レート日：'
  },
  'en-US': {
    loading: 'Loading reference rates; showing CNY',
    unavailable: 'Rates unavailable; showing CNY',
    reference: 'Reference rate date: '
  }
};

function validAmount(value) {
  if (typeof value !== 'number' && typeof value !== 'string') {
    return null;
  }
  if (typeof value === 'string' && !value.trim()) return null;
  const number = Number(value);
  return Number.isFinite(number) ? number : null;
}

function targetCurrency(locale) {
  if (locale === 'ja-JP') return 'JPY';
  if (locale === 'en-US') return 'USD';
  return 'CNY';
}

async function refreshRates() {
  const now = Date.now();
  if (state.loading || now - state.lastAttempt < 60000) return;

  state.lastAttempt = now;
  state.loading = true;
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), 8000);

  try {
    const response = await fetch(
      'https://api.frankfurter.dev/v2/rates?base=CNY&quotes=JPY,USD',
      {
        signal: controller.signal,
        credentials: 'omit',
        referrerPolicy: 'no-referrer'
      }
    );
    if (!response.ok) throw new Error('RATE_HTTP_ERROR');

    const rows = await response.json();
    if (!Array.isArray(rows)) throw new Error('RATE_FORMAT_ERROR');

    const next = {};
    ['JPY', 'USD'].forEach(currency => {
      const row = rows.find(item =>
        item.base === 'CNY' && item.quote === currency
      );
      if (
        !row ||
        typeof row.rate !== 'number' ||
        !Number.isFinite(row.rate) ||
        row.rate <= 0 ||
        !/^\d{4}-\d{2}-\d{2}$/.test(row.date)
      ) {
        throw new Error('INVALID_RATE');
      }
      next[currency] = { rate: row.rate, date: row.date };
    });

    state.rates = next;
  } catch (error) {
    // Do not relabel an unconverted CNY amount as JPY or USD.
    state.rates = null;
  } finally {
    clearTimeout(timer);
    state.loading = false;
  }
}

export default {
  install() {
        Vue.prototype.$withdrawQuote = function () {
      const currency = targetCurrency(this.$i18n.locale);
      if (currency === 'CNY') {
        return { currency: 'CNY', rate: 1 };
      }

      const row = state.rates && state.rates[currency];
      if (!row) return null;

      const timestamp = Date.parse(row.date + 'T00:00:00Z');
      const age = Date.now() - timestamp;
      if (
        !Number.isFinite(timestamp) ||
        age < -86400000 ||
        age > 7 * 86400000
      ) {
        return null;
      }

      return {
        currency: currency,
        rate: row.rate,
        date: row.date
      };
    };
    Vue.prototype.$displayMoney = function (value) {
      const amount = validAmount(value);
      if (amount === null) return '--';

      const currency = targetCurrency(this.$i18n.locale);
      if (currency === 'CNY') return amount.toFixed(2) + '元';

      const rate = state.rates && state.rates[currency];
      if (!rate) return amount.toFixed(2) + ' CNY';

      const converted = amount * rate.rate;
      if (!Number.isFinite(converted)) return '--';

      if (currency === 'JPY') {
        return converted.toLocaleString('ja-JP', {
          minimumFractionDigits: 0,
          maximumFractionDigits: 0
        }) + '円';
      }

      return '$' + converted.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    };

    Vue.prototype.$currencyNote = function () {
      const locale = this.$i18n.locale;
      const currency = targetCurrency(locale);
      if (currency === 'CNY') return '';

      const text = messages[locale] || messages['en-US'];
      const rate = state.rates && state.rates[currency];
      if (rate) return '';
      return state.loading ? text.loading : text.unavailable;
    };

    refreshRates();
    window.setInterval(refreshRates, 60 * 60 * 1000);
    window.addEventListener('online', refreshRates);
  }
};